// Constants
import { OperationStatus } from "@ms/planner-models/lib/local/client/operations/Enumerations";
import { DefaultContainerPermissions, DefaultPlanPermissions } from "@ms/planner-models/lib/local/constants/PermissionsConstants";
import { ContextScenarioConstants } from "@ms/uno-constants/lib/local/ExternalSourceConstants";
import { ContainerType } from "@ms/planner-models/lib/local/service/planner/enumerations/IPlannerEnumerations";
// Utilities
import { canAccessPlanViaContainers, canCurrentUserAccessContainer, getIsUserContainerOwner } from "./ContainerUtilities";
/**
 * Get the plan permissions, using data from multiple stores
 * @param plan The plan to get permissions for, if loaded
 * @param stores The stores to use to calculate the plan permissions
 * @param traceLogger Trace event logger
 */ export function getPlanPermissionsFromStores(plan, stores, traceLogger) {
    const { permissionsStore, appContextStore } = stores;
    if (plan === null) {
        return DefaultPlanPermissions;
    }
    const currentUserId = appContextStore.getAppContext().userId;
    const isOwner = currentUserId ? getIsUserContainerOwner(currentUserId, plan.container, stores) : false;
    return permissionsStore.getPlanPermissions(plan, canAccessPlanViaContainers(plan, stores, traceLogger), isOwner, getIsPlanCreationInProgress(plan.id, stores));
}
/**
 * Get the task permissions, using data from multiple stores
 * @param task The task to get permissions for
 * @param plan The plan to get permissions for, if loaded
 * @param stores The stores to use to calculate the task permissions
 * @param traceLogger Trace event logger
 */ export function getTaskPermissionsFromStores(task, plan, stores, traceLogger) {
    const { permissionsStore } = stores;
    return permissionsStore.getTaskPermissions(task, plan, plan ? canAccessPlanViaContainers(plan, stores, traceLogger) : true);
}
/**
 * Get the Checklist Item permissions, using data from multiple stores
 * @param task The task to get the checklist item permissions for
 * @param plan The plan to get permissions for, if loaded
 * @param stores The stores to use to calculate the checklist item permissions
 * @param traceLogger Trace event logger
 */ export function getChecklistItemPermissionsFromStores(task, plan, stores, traceLogger) {
    const { permissionsStore } = stores;
    return permissionsStore.getChecklistItemPermissions(task, plan, plan ? canAccessPlanViaContainers(plan, stores, traceLogger) : true);
}
/**
 * Get the Attachment permissions, using data from multiple stores
 * @param task The task to get the attachment permissions for
 * @param plan The plan to get permissions for, if loaded
 * @param stores The stores to use to calculate the attachment permissions
 * @param traceLogger Trace event logger
 */ export function getAttachmentPermissionsFromStores(task, plan, stores, traceLogger) {
    const { permissionsStore } = stores;
    return permissionsStore.getAttachmentPermissions(task, plan, plan ? canAccessPlanViaContainers(plan, stores, traceLogger) : true);
}
/**
 * Get the container permissions, using data from multiple stores
 * @param container The container to get permissions for, if loaded
 * @param stores The stores to use to calculate the container permissions
 * @param traceLogger Trace event logger
 */ export function getContainerPermissionsFromStores(container, stores, traceLogger) {
    const { appContextStore, permissionsStore, modernGroupsStore } = stores;
    if (container === null) {
        return DefaultContainerPermissions;
    }
    const currentUserId = appContextStore.getAppContext().userId;
    return permissionsStore.getContainerPermissions(container, canCurrentUserAccessContainer(container, stores, traceLogger), currentUserId ? getIsUserContainerOwner(currentUserId, container, stores) : false, modernGroupsStore.getModernGroup(container.containerId).data?.visibility);
}
export function getIsPlanCreationInProgress(planId, stores) {
    const { planStore } = stores;
    const planCreationOperationStatus = planStore.getCreatePlanFromTemplateOperationStatus(planId);
    return planCreationOperationStatus === OperationStatus.Running;
}
/**
 * Check if the plan can access goals based on user permissions, container permissions, and plan creation info
 * @param plan The plan to check goals access for
 * @param canUserAccessGoals Whether the user has access to goals (from userPermissions.canAccessGoals)
 * @param configProvider The config provider to get flight flags from
 * @param containerGoalsPermissions Whether the container allows using goals (from containerPermissions.canUseGoals)
 * @returns Whether the plan can access goals
 */ export function canPlanAccessGoals(plan, canUserAccessGoals, configProvider, containerGoalsPermissions) {
    // Check if the user has copilot access and if the container supports FAB and goals in plan
    return getIsGoalsEnabled(plan, canUserAccessGoals, configProvider) && containerGoalsPermissions;
}
/**
 * Get whether goals are enabled for a plan
 * @param plan The plan to check goals access for
 * @param canUserAccessGoals Whether the user has access to goals
 * @param configProvider The config provider to get flight flags from
 * @returns Whether goals are enabled for the plan
 */ export function getIsGoalsEnabled(plan, canUserAccessGoals, configProvider) {
    const config = configProvider();
    // If plan was created by Teams Channel Agent, goals is enabled for such plans
    const isPlanCreatedByTeamsChannelAgent = plan && plan.creationInfo.scenarioId === ContextScenarioConstants.teamsChannelAgentContext && config.flights.EnableGoalsInWorkbackPlan;
    // Goals is enabled if user can access goals and either the flight is enabled or the plan was created by Teams Channel Agent
    const isGoalsEnabled = canUserAccessGoals && (config.flights.EnableGoalsInPlan || isPlanCreatedByTeamsChannelAgent);
    return !!isGoalsEnabled;
}
/**
 * Check if goals feature should be shown for a plan (independent of user edit permissions).
 * This enables read-only goals access for M365 users while P1+/Copilot users get full edit access.
 * @param plan The plan to check goals feature visibility for
 * @param configProvider The config provider to get flight flags from
 * @param containerGoalsPermissions Whether the container allows using goals (from containerPermissions.canUseGoals)
 * @returns Whether the goals feature should be shown
 */ export function canPlanShowGoals(plan, configProvider, containerGoalsPermissions) {
    const config = configProvider();
    // Goals are never shown in ToDo container
    if (plan.container.containerType === ContainerType.ToDo) {
        return false;
    }
    // If plan was created by Teams Channel Agent, goals is enabled for such plans
    const isPlanCreatedByTeamsChannelAgent = plan && plan.creationInfo.scenarioId === ContextScenarioConstants.teamsChannelAgentContext && config.flights.EnableGoalsInWorkbackPlan;
    // Goals feature is shown if either the flight is enabled or the plan was created by Teams Channel Agent
    const isGoalsFlightEnabled = config.flights.EnableGoalsInPlan || isPlanCreatedByTeamsChannelAgent;
    return !!(plan && isGoalsFlightEnabled && containerGoalsPermissions);
}
